import React, { useState } from "react";
import "../surveys/createSurvey.css";
import Footer from "../../components/navbar/Footer";
import Navbar from "../../components/navbar/Navbar";
import { Box, Button, Modal, Typography, useMediaQuery } from "@mui/material";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, ContentCopy } from "@mui/icons-material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: 420,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
};

export default function Result() {
  const silverSurveyData = JSON.parse(localStorage.getItem("silverSurveyData"));
  const navigate = useNavigate();
  const location = useLocation();
  const { pass } = location.state || false;
  const [isLoadingCertificate, setIsLoadingCertificate] = useState(false);
  const [appointmentScheduled, setAppointmentScheduled] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [appointmentTime, setAppointmentTime] = useState("");
  const [silverPreRegistered, setSilverPreRegistered] = useState(false);
  const id = localStorage.getItem("surveyRecordId") || null;
  const email = localStorage.getItem("email") || "";
  const [open, setOpen] = useState(false);
  const [reportUrl, setReportUrl] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  // Responsive breakpoints
  const isMobile = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1024px)");

  const PopupBlockedAlert = () => (
    <div
      style={{
        backgroundColor: "#fff3cd",
        border: "1px solid #ffeaa7",
        borderRadius: "12px",
        padding: "16px 20px",
        marginTop: "24px",
        width: "100%",
        maxWidth: "600px",
        margin: "24px auto 0 auto",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        boxSizing: "border-box",
      }}
    >
      <p
        style={{
          fontSize: isMobile ? "13px" : "14px",
          color: "#856404",
          margin: 0,
          lineHeight: "1.5",
          fontWeight: "500",
        }}
      >
        If you experience issues viewing your report, or if the download does
        not start when you click <strong>Download Report</strong>, please copy
        the link below and paste it into a new browser window to access your
        report:
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          onClick={handleCopyLink}
          disabled={!reportUrl}
          startIcon={linkCopied ? <CheckCircle /> : <ContentCopy />}
          sx={{
            fontSize: isMobile ? "12px" : "13px",
            fontWeight: "600",
            textTransform: "capitalize",
            borderRadius: "20px",
            padding: isMobile ? "6px 12px" : "8px 16px",
            whiteSpace: "nowrap",
            borderColor: linkCopied ? "#28a745" : "#6c757d",
            color: linkCopied ? "#28a745" : "#6c757d",
            backgroundColor: linkCopied ? "#f8fff9" : "transparent",
            "&:hover": {
              borderColor: linkCopied ? "#218838" : "#495057",
              backgroundColor: linkCopied ? "#f1f8f2" : "#f8f9fa",
            },
            "&:disabled": {
              borderColor: "#dee2e6",
              color: "#6c757d",
            },
          }}
        >
          {linkCopied ? "Link Copied!" : "Copy Link"}
        </Button>
      </div>
    </div>
  );

  const handleDownloadReport = async () => {
    setIsLoadingReport(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/getDashboard/${id}`
      );
      if (response.data) {
        setReportUrl(response.data);
        setTimeout(() => {
          setIsLoadingReport(false);
          window.open(response.data, "_blank");
        }, 4000);
      }
    } catch (error) {
      console.error("Error fetching Report:", error);
    }
  };

  const handleCopyLink = async () => {
    if (!reportUrl) return;

    try {
      await navigator.clipboard.writeText(reportUrl);
      setLinkCopied(true);

      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
    } catch (err) {
      console.error("Failed to copy link: ", err);
    }
  };

  const handleDownloadCertificate = async () => {
    setIsLoadingCertificate(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/getCertificate/${id}`
      );
      if (response.data) {
        setTimeout(() => window.open(response.data, "_blank"), 1000);
      }
    } catch (error) {
      console.error("Error fetching Report:", error);
    } finally {
      setTimeout(() => setIsLoadingCertificate(false), 1000);
    }
  };

  const handlePreRegisterSilver = async () => {
    if (!email) {
      console.error("Email not found.", email);
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/addSilverRegistration`,
        new URLSearchParams({ email }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      if (response.data) {
        setSilverPreRegistered(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleScheduleAppointment = () => {
    if (appointmentTime.trim() !== "") {
      setAppointmentTime(appointmentTime);
      setAppointmentScheduled(true);
    }
  };

  // Enhanced responsive styles
  const baseBlockStyle = {
    padding: isMobile ? "20px 16px" : isTablet ? "24px 20px" : "28px 24px",
    border: "1px solid #e0e0e0",
    borderRadius: "16px",
    boxSizing: "border-box",
    backgroundColor: "#fafafa",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    transition: "all 0.3s ease",
    width: "100%",
    minHeight: "auto",
  };

  const successBlockStyle = {
    ...baseBlockStyle,
    backgroundColor: "#f0f9f0",
    border: "1px solid #4caf50",
    boxShadow: "0 4px 16px rgba(76,175,80,0.15)",
  };

  // Main container style - responsive
  const containerStyle = {
    padding: isMobile
      ? "20px 16px 120px 16px"
      : isTablet
      ? "40px 24px 120px 24px"
      : "60px 32px 120px 32px",
    textAlign: "center",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "1200px",
    minHeight: "calc(100vh - 140px)",
    boxSizing: "border-box",
  };

  // Content wrapper to push footer down
  const contentWrapperStyle = {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
  };

  // Pre-register block container (only shown on pass)
  const preRegisterContainerStyle = {
    width: "100%",
    maxWidth: "600px",
    margin: "32px auto 32px auto",
    display: "block",
  };

  // Download section container
  const downloadSectionStyle = {
    width: "100%",
    maxWidth: "600px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  };

  return pass ? (
    <div
      className="create-container"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <Navbar />
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={containerStyle}>
          <div style={contentWrapperStyle}>
            {/* Header Section */}
            <div style={{ marginBottom: "40px", width: "100%" }}>
              <h1
                style={{
                  fontWeight: "600",
                  fontSize: isMobile ? "20px" : isTablet ? "24px" : "28px",
                  textTransform: "capitalize",
                  color: "#2c3e50",
                  marginBottom: "12px",
                  letterSpacing: "0.5px",
                  lineHeight: "1.3",
                }}
              >
                Thank you for completing the ISP Awards Bronze Level
                Questionnaire
              </h1>

              <h2
                style={{
                  fontWeight: "700",
                  fontSize: isMobile ? "24px" : isTablet ? "30px" : "36px",
                  textTransform: "capitalize",
                  color: "#2c3e50",
                  marginBottom: "16px",
                  textShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  lineHeight: "1.2",
                }}
              >
                Congratulations!
              </h2>

              <p
                style={{
                  fontSize: isMobile ? "15px" : "17px",
                  color: "#34495e",
                  fontWeight: "500",
                  lineHeight: "1.6",
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                Based on the information you provided, we confirm that your
                organisation invests in sales. Your certificate will be issued
                by The ISP, and their team will contact you shortly.
                Additionally, you can download a detailed report that highlights
                your strengths and areas for improvement.
              </p>
            </div>
            {/* Download Section */}
            <div style={downloadSectionStyle}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDownloadReport}
                sx={{
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: 700,
                  padding: isMobile ? "12px 24px" : "14px 36px",
                  textTransform: "capitalize",
                  borderRadius: "30px",
                  backgroundColor: "#24a9dd",
                  boxShadow: "0 4px 15px rgba(36,169,221,0.3)",
                  minWidth: isMobile ? "200px" : "240px",
                  "&:hover": {
                    backgroundColor: "#1a8bb8",
                    boxShadow: "0 6px 20px rgba(36,169,221,0.4)",
                  },
                }}
              >
                {isLoadingReport ? "Downloading..." : "Download Report"}
              </Button>

              {reportUrl && <PopupBlockedAlert />}
            </div>
            {/* Pre-registration Section - Only for Pass */}
            {!silverSurveyData?.survey_key && (
              <div style={preRegisterContainerStyle}>
                <div
                  style={
                    silverPreRegistered ? successBlockStyle : baseBlockStyle
                  }
                >
                  {!silverPreRegistered ? (
                    <>
                      <h3
                        style={{
                          fontWeight: "600",
                          fontSize: isMobile ? "17px" : "19px",
                          color: "#2c3e50",
                          marginBottom: "12px",
                          lineHeight: "1.3",
                        }}
                      >
                        Pre-register for Our Silver Level Program
                      </h3>
                      <p
                        style={{
                          fontSize: isMobile ? "14px" : "15px",
                          color: "#555",
                          marginBottom: "20px",
                          lineHeight: "1.5",
                        }}
                      >
                        We are excited to announce our upcoming Silver Level
                        program. Would you like to pre-register for exclusive
                        early access?
                      </p>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePreRegisterSilver}
                        sx={{
                          fontSize: isMobile ? "13px" : "14px",
                          padding: isMobile ? "10px 20px" : "12px 24px",
                          textTransform: "capitalize",
                          borderRadius: "25px",
                          backgroundColor: "#2691a3",
                          fontWeight: "600",
                          boxShadow: "0 3px 10px rgba(38,145,163,0.3)",
                          "&:hover": {
                            backgroundColor: "#1e7a8a",
                            boxShadow: "0 4px 15px rgba(38,145,163,0.4)",
                          },
                        }}
                      >
                        Yes, Pre-register
                      </Button>
                    </>
                  ) : (
                    <>
                      <h3
                        style={{
                          fontWeight: "600",
                          fontSize: isMobile ? "17px" : "19px",
                          color: "#27ae60",
                          marginBottom: "12px",
                          lineHeight: "1.3",
                        }}
                      >
                        ✓ Pre-registration Successful
                      </h3>
                      <p
                        style={{
                          fontSize: isMobile ? "14px" : "15px",
                          color: "#2d5016",
                          lineHeight: "1.5",
                          fontWeight: "500",
                        }}
                      >
                        Thank you for pre-registering for silver level. We will
                        contact you soon.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div style={{ marginTop: "auto" }}>
          <Footer />
        </div>
      </div>

      {/* Modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "#2c3e50",
              fontSize: isMobile ? "18px" : "20px",
            }}
          >
            Registration Email Sent
          </Typography>
          <Typography
            id="modal-modal-description"
            sx={{
              mb: 3,
              lineHeight: 1.6,
              color: "#555",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            A registration link has been sent to your email. Please check your
            inbox and click on the link to complete the company registration
            process.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpen(false)}
            sx={{
              display: "block",
              ml: "auto",
              borderRadius: "20px",
              backgroundColor: "#2691a3",
              fontWeight: 600,
              fontSize: isMobile ? "13px" : "14px",
              padding: isMobile ? "8px 16px" : "10px 20px",
              "&:hover": {
                backgroundColor: "#1e7a8a",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </div>
  ) : (
    // FAIL STATE
    <div
      className="create-container"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <Navbar />
      <div
        style={{
          flex: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={containerStyle}>
          <div style={contentWrapperStyle}>
            {/* Header Section - Fail */}
            <div style={{ marginBottom: "40px", width: "100%" }}>
              <h1
                style={{
                  fontWeight: "600",
                  fontSize: isMobile ? "20px" : isTablet ? "24px" : "28px",
                  textTransform: "capitalize",
                  color: "#2c3e50",
                  marginBottom: "20px",
                  letterSpacing: "0.5px",
                  lineHeight: "1.3",
                }}
              >
                Thank you for completing the assessment.
              </h1>

              <p
                style={{
                  fontSize: isMobile ? "15px" : "17px",
                  color: "#2c3e50",
                  fontWeight: "500",
                  lineHeight: "1.6",
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                Based on the information provided, your organisation has not
                fully met the qualifying criteria at this stage. However, you're
                making valuable progress. You can download your personalised
                report to see where you're performing well and where there's
                room for improvement. We encourage you to address those areas
                and try again in three months.
              </p>
            </div>

            {/* Download Section - Fail */}
            <div style={downloadSectionStyle}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDownloadReport}
                sx={{
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: 600,
                  padding: isMobile ? "12px 24px" : "14px 36px",
                  textTransform: "capitalize",
                  borderRadius: "30px",
                  backgroundColor: "#24a9dd",
                  boxShadow: "0 4px 15px rgba(36,169,221,0.3)",
                  minWidth: isMobile ? "200px" : "240px",
                  "&:hover": {
                    backgroundColor: "#1a8bb8",
                  },
                }}
              >
                {isLoadingReport ? "Downloading..." : "Download Report"}
              </Button>

              {reportUrl && <PopupBlockedAlert />}
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div style={{ marginTop: "auto" }}>
          <Footer />
        </div>
      </div>
    </div>
  );
}
